/**
 * /admin/models — Model Portfolios (M1: buckets; migration 0123, C-302).
 *
 * Pedro 2026-10-26 (OPEN_ITEMS "M1-PLAN"): a separate Models card (MQ2), read by whoever holds
 * `portfolios.read` (MQ1), changed by `model.manage` — "only admin and specially privileged
 * users can change. no maker-checker" (MQ6). A bucket is a reusable set of Approved securities
 * and cash lines (one per currency, MQ4 / MF4) whose weights add up to 100 %: typed with 2
 * decimals; within 0.05 % of 100 % the app spreads the difference (MF1 (a)). A member that is
 * no longer Approved shows red, and so does its bucket; the bucket can still be used (MQ5).
 *
 * M2 (migration 0124, Pedro "start M2"): the Models tab — a model is buckets weighted as % of
 * the whole model, adding up to 100 % (same typing and 0.05 % rule); red when one of its buckets
 * is; a bucket used by a model cannot be deleted (red warning, Delete greyed).
 *
 * Edit → Save, like every record (§3g Pattern A); Delete bucket is its own confirmed action,
 * available in Edit mode only (C-172).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Container, Group, Modal, NumberInput, Select, Stack, Table,
  Tabs, Text, TextInput, Textarea, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconBucket, IconDownload, IconEye, IconPlus, IconStack2, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { modelsApi, type Bucket, type BucketMember, type BucketMemberKind, type ModelPortfolio } from '../api/models';
import { ModelViewTab } from './ModelViewTab';
import { securitiesApi } from '../api/securities';
import { securityLink } from '../utils/securityLink';
import { BackToCompliance } from '../components/BackToCompliance';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { RequireAccess } from '../components/RequireAccess';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePermissions } from '../hooks/usePermissions';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const NO_MANAGE = 'Changing buckets needs the "model.manage" permission (Admin by default; granted per person in Admin → Permissions).';
// Offered for cash lines besides the currencies of the securities master (MF4: one per currency).
const COMMON_CCYS = ['EUR', 'USD', 'GBP', 'CHF', 'JPY'];

export function ModelsPage() {
  useDocumentTitle('Models');
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') ?? (user?.role === 'admin' ? 'admin' : 'welcome');
  const [tab, setTab] = useState<string | null>(searchParams.get('tab') ?? 'view');
  // A bucket name in a model opens that bucket on the Build buckets tab.
  const [bucketRequest, setBucketRequest] = useState<{ id: string; n: number } | null>(null);
  const openBucket = (id: string) => { setBucketRequest((r) => ({ id, n: (r?.n ?? 0) + 1 })); setTab('buckets'); };
  return (
    <RequireAccess permission="portfolios.read">
      <Container size="xl">
        <Stack gap="sm">
          <Group gap="md" align="center" wrap="nowrap">
            {from === 'admin'
              ? <BackToCompliance to="/admin" label="Back to Admin" />
              : <BackToCompliance to="/" label="Back to Welcome" />}
            <Tooltip withArrow multiline w={360} label="Model portfolios: models built from buckets, and the buckets themselves (reusable sets of Approved securities and cash), each adding up to 100%.">
              <Title order={3}>Models</Title>
            </Tooltip>
          </Group>
          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List>
              {/* Pedro 2026-11-01: a View model tab (first, as View and trade portfolio is on Portfolios),
                  and the two build tabs renamed. */}
              <Tabs.Tab value="view" leftSection={<IconEye size={14} />}>View model</Tabs.Tab>
              <Tabs.Tab value="models" leftSection={<IconStack2 size={14} />}>Build models</Tabs.Tab>
              <Tabs.Tab value="buckets" leftSection={<IconBucket size={14} />}>Build buckets</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="view" pt="md"><ModelViewTab /></Tabs.Panel>
            <Tabs.Panel value="models" pt="md"><ModelsTab onOpenBucket={openBucket} /></Tabs.Panel>
            <Tabs.Panel value="buckets" pt="md"><BucketsTab request={bucketRequest} /></Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </RequireAccess>
  );
}

// ── Buckets ──────────────────────────────────────────────────────────────────

function BucketsTab({ request }: { request: { id: string; n: number } | null }) {
  const { can } = usePermissions();
  const canManage = can('model.manage');
  const { confirmDiscardAsync } = useDirtyForm();
  const listQ = useQuery({ queryKey: ['models', 'buckets'], queryFn: () => modelsApi.buckets() });
  const items = listQ.data?.items ?? [];
  // null = nothing open; 'new' = a new bucket being written; else a bucket id.
  const [open, setOpen] = useState<string | null>(request?.id ?? null);
  useEffect(() => { if (request) setOpen(request.id); }, [request]);
  const dirtyRef = useRef(false);
  const choose = async (next: string | null) => {
    if (next === open) return;
    if (dirtyRef.current && !(await confirmDiscardAsync())) return;
    setOpen(next);
  };
  const current = open && open !== 'new' ? items.find((b) => b.id === open) ?? null : null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          A bucket is a reusable set of Approved securities and cash lines whose weights add up to 100%.
        </Text>
        <DisabledTooltipButton leftSection={<IconPlus size={14} />} size="xs"
          disabledReason={canManage ? undefined : NO_MANAGE} onClick={() => void choose('new')}>
          New bucket
        </DisabledTooltipButton>
      </Group>
      <Card withBorder padding={0}>
        <Table highlightOnHover verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Bucket</Table.Th>
              <Table.Th>Members</Table.Th>
              <Table.Th>Used in models</Table.Th>
              <Table.Th>Description</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {listQ.isLoading && <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">Loading…</Text></Table.Td></Table.Tr>}
            {!listQ.isLoading && items.length === 0 && (
              <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">No buckets yet.</Text></Table.Td></Table.Tr>
            )}
            {items.map((b) => (
              <Table.Tr key={b.id} onClick={() => void choose(b.id)}
                style={{ cursor: 'pointer', background: open === b.id ? 'var(--mantine-color-blue-0)' : undefined }}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500} c={b.flagged.length ? 'red' : undefined}>{b.name}</Text>
                    {b.flagged.length > 0 && <FlagBadge bucket={b} />}
                  </Group>
                </Table.Td>
                <Table.Td><Text size="sm">{membersText(b)}</Text></Table.Td>
                <Table.Td><Text size="sm">{b.used_in_models.map((m) => m.name).join(', ') || '—'}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{b.description ?? ''}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
      {open && (open === 'new' || current) && (
        <BucketEditor key={open} bucket={current} canManage={canManage}
          onDirty={(d) => { dirtyRef.current = d; }}
          onSaved={(b) => setOpen(b.id)} onDeleted={() => setOpen(null)} onClose={() => void choose(null)} />
      )}
    </Stack>
  );
}

function membersText(b: Bucket): string {
  const parts = [];
  if (b.security_count) parts.push(`${b.security_count} securit${b.security_count === 1 ? 'y' : 'ies'}`);
  if (b.cash_count) parts.push(`${b.cash_count} cash`);
  return parts.join(' · ') || '—';
}

function FlagBadge({ bucket }: { bucket: Bucket }) {
  return (
    <Tooltip withArrow multiline w={340}
      label={`Not approved: ${bucket.flagged.join(', ')}. The bucket can still be used; replace or remove ${bucket.flagged.length === 1 ? 'it' : 'them'} when ready.`}>
      <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={11} />}>
        {bucket.flagged.length} not approved
      </Badge>
    </Tooltip>
  );
}

interface Row {
  key: string;
  kind: BucketMemberKind;
  security_id: string | null;
  cash_currency: string | null;
  name: string;
  isin: string | null;
  currency: string | null;
  weight: string;
  flag: string | null;
}

const toRows = (members: BucketMember[]): Row[] => members.map((m) => ({
  key: m.kind === 'cash' ? `cash:${m.cash_currency}` : `sec:${m.security_id}`,
  kind: m.kind, security_id: m.security_id, cash_currency: m.cash_currency,
  name: m.name ?? m.isin ?? '', isin: m.isin, currency: m.kind === 'cash' ? m.cash_currency : m.currency,
  weight: Number(m.weight_entered).toFixed(2), flag: m.flag,
}));

/** Typed weights in hundredths (cents of a %), exact for 2 decimals. */
const hundredths = (w: string) => {
  const n = Number(w);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};
const fmtPct = (h: number) => `${(h / 100).toFixed(2)}%`;

function BucketEditor({ bucket, canManage, onDirty, onSaved, onDeleted, onClose }: {
  bucket: Bucket | null;          // null = new
  canManage: boolean;
  onDirty: (dirty: boolean) => void;
  onSaved: (b: Bucket) => void;
  onDeleted: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { registerDirty, confirmDiscardAsync } = useDirtyForm();
  const isNew = bucket === null;
  const [editing, setEditing] = useState(isNew);
  const [name, setName] = useState(bucket?.name ?? '');
  const [description, setDescription] = useState(bucket?.description ?? '');
  const [rows, setRows] = useState<Row[]>(() => toRows(bucket?.members ?? []));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toleranceH = 5;   // 0.05 % (the server's TOLERANCE; it re-checks on Save)

  // Read mode follows the stored bucket (after a save, or another user's change).
  useEffect(() => {
    if (editing || !bucket) return;
    setName(bucket.name); setDescription(bucket.description ?? ''); setRows(toRows(bucket.members));
  }, [bucket, editing]);

  const original = useMemo(() => JSON.stringify({ n: bucket?.name ?? '', d: bucket?.description ?? '', r: toRows(bucket?.members ?? []) }), [bucket]);
  const dirty = editing && JSON.stringify({ n: name, d: description, r: rows }) !== original;
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);

  const secsQ = useQuery({ queryKey: ['securities', 'list', 'all'], queryFn: () => securitiesApi.list({}), enabled: editing });
  const inBucket = new Set(rows.map((r) => r.key));
  const addable = useMemo(() => (secsQ.data?.items ?? [])
    .filter((s) => s.authorized && !inBucket.has(`sec:${s.id}`))
    .map((s) => ({ value: s.id, label: [s.isin, s.description, s.bb_ticker, s.figi, s.currency].filter(Boolean).join(' · ') })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [secsQ.data, rows]);
  const ccys = useMemo(() => {
    const all = new Set(COMMON_CCYS);
    for (const s of secsQ.data?.items ?? []) if (/^[A-Z]{3}$/.test(s.currency) && s.currency !== 'XXX') all.add(s.currency);
    return [...all].sort().filter((c) => !inBucket.has(`cash:${c}`));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secsQ.data, rows]);

  const totalH = rows.reduce((t, r) => t + (Number.isNaN(hundredths(r.weight)) ? 0 : hundredths(r.weight)), 0);
  const badWeight = rows.find((r) => !(hundredths(r.weight) > 0 && hundredths(r.weight) <= 10000));
  const gapH = 10000 - totalH;
  const flagged = rows.filter((r) => r.flag);

  const saveReason = !name.trim() ? 'Give the bucket a name.'
    : rows.length === 0 ? 'Add at least one security or cash line.'
    : badWeight ? `Each weight must be above 0% and at most 100% (${badWeight.name}).`
    : Math.abs(gapH) > toleranceH ? `The weights add up to ${fmtPct(totalH)}; they must be within 0.05% of 100%.`
    : undefined;

  const save = async (): Promise<boolean> => {
    if (saveReason) { notifications.show({ color: 'red', title: 'Not saved', message: saveReason }); return false; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(), description: description.trim() || null,
        members: rows.map((r) => ({ kind: r.kind, security_id: r.security_id, cash_currency: r.cash_currency,
          weight: (hundredths(r.weight) / 100).toFixed(2) })),
      };
      const saved = isNew ? await modelsApi.createBucket(body) : await modelsApi.updateBucket(bucket!.id, body);
      // ['models'] = buckets and models: a model shows its buckets' names and red flags (C-204).
      await qc.invalidateQueries({ queryKey: ['models'] });
      notifications.show({ color: 'green', title: 'Saved', message: gapH !== 0
        ? `${saved.name} saved; the ${fmtPct(Math.abs(gapH))} difference was spread over the members so they add up to 100%.`
        : `${saved.name} saved.` });
      setName(saved.name); setDescription(saved.description ?? ''); setRows(toRows(saved.members));
      setEditing(false); onDirty(false);
      onSaved(saved);
      return true;
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: errMsg(e) });
      return false;
    } finally { setSaving(false); }
  };
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => registerDirty('model-bucket-editor', dirty, () => saveRef.current()), [dirty, registerDirty]);

  const discard = async () => {
    if (dirty && !(await confirmDiscardAsync())) return;
    if (isNew) { onDirty(false); onClose(); return; }
    setName(bucket!.name); setDescription(bucket!.description ?? ''); setRows(toRows(bucket!.members)); setEditing(false);
  };
  const doDelete = async () => {
    try {
      await modelsApi.deleteBucket(bucket!.id);
      await qc.invalidateQueries({ queryKey: ['models'] });
      notifications.show({ color: 'green', title: 'Deleted', message: `${bucket!.name} was deleted.` });
      setConfirmDelete(false); onDirty(false); onDeleted();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: errMsg(e) });
    }
  };

  const addSecurity = (id: string | null) => {
    const s = (secsQ.data?.items ?? []).find((x) => x.id === id);
    if (!s) return;
    setRows((p) => [...p, { key: `sec:${s.id}`, kind: 'security', security_id: s.id, cash_currency: null,
      name: s.description, isin: s.isin, currency: s.currency, weight: '', flag: null }]);
  };
  const addCash = (ccy: string | null) => {
    if (!ccy) return;
    setRows((p) => [...p, { key: `cash:${ccy}`, kind: 'cash', security_id: null, cash_currency: ccy,
      name: `Cash ${ccy}`, isin: null, currency: ccy, weight: '', flag: null }]);
  };
  const setWeight = (key: string, v: string | number) =>
    setRows((p) => p.map((r) => (r.key === key ? { ...r, weight: String(v) } : r)));
  const remove = (key: string) => setRows((p) => p.filter((r) => r.key !== key));

  const totalColor = totalH === 10000 ? 'green' : Math.abs(gapH) <= toleranceH ? 'yellow.8' : 'red';
  const inModels = bucket?.used_in_models ?? [];
  const resolved = new Map((bucket?.members ?? []).map((m) => [m.kind === 'cash' ? `cash:${m.cash_currency}` : `sec:${m.security_id}`, m.weight_pct]));

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          {editing ? (
            <Stack gap={6} style={{ flex: 1, maxWidth: 640 }}>
              <TextInput label="Bucket name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
              <Textarea label="Description" autosize minRows={1} value={description}
                onChange={(e) => setDescription(e.currentTarget.value)} />
            </Stack>
          ) : (
            <Stack gap={2}>
              <Title order={4} c={flagged.length ? 'red' : undefined}>{bucket?.name}</Title>
              {bucket?.description && <Text size="sm" c="dimmed">{bucket.description}</Text>}
              {bucket && bucket.used_in_models.length > 0 && (
                <Text size="xs" c="dimmed">Used in models: {bucket.used_in_models.map((m) => m.name).join(', ')}</Text>
              )}
            </Stack>
          )}
          <Group gap="xs">
            {editing ? (
              <>
                {!isNew && (
                  <Button size="xs" color="red" variant="subtle" leftSection={<IconTrash size={14} />}
                    onClick={() => setConfirmDelete(true)}>Delete bucket</Button>
                )}
                <Button size="xs" variant="default" onClick={() => void discard()}>Discard</Button>
                <DisabledTooltipButton size="xs" loading={saving} disabledReason={saveReason ?? (!dirty && !isNew ? 'Nothing has changed.' : undefined)}
                  onClick={() => void save()}>Save changes</DisabledTooltipButton>
              </>
            ) : (
              <>
                <Button size="xs" variant="default" onClick={onClose}>Close</Button>
                <DisabledTooltipButton size="xs" disabledReason={canManage ? undefined : NO_MANAGE}
                  onClick={() => setEditing(true)}>Edit</DisabledTooltipButton>
              </>
            )}
          </Group>
        </Group>

        {flagged.length > 0 && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            {flagged.length === 1 ? '1 member is' : `${flagged.length} members are`} no longer Approved on Securities
            ({flagged.map((r) => r.name).join(', ')}). The bucket can still be used; replace or remove them when ready.
          </Alert>
        )}

        <Table verticalSpacing={4} withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Member</Table.Th>
              <Table.Th>ISIN</Table.Th>
              <Table.Th>Ccy</Table.Th>
              <Table.Th style={{ textAlign: 'right', width: 150 }}>Weight (% of bucket)</Table.Th>
              {editing && <Table.Th style={{ width: 40 }} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && (
              <Table.Tr><Table.Td colSpan={editing ? 5 : 4}><Text size="sm" c="dimmed">No members yet — add a security or a cash line below.</Text></Table.Td></Table.Tr>
            )}
            {rows.map((r) => {
              const kept = resolved.get(r.key);
              const differs = !editing && kept != null && Number(kept).toFixed(12) !== Number(r.weight).toFixed(12);
              return (
                <Table.Tr key={r.key} style={r.flag ? { background: 'var(--mantine-color-red-0)' } : undefined}>
                  <Table.Td>
                    {/* M1-LINK (Pedro 2026-10-26): a security's name opens it in Securities. */}
                    {r.kind === 'security' && r.security_id ? (
                      <Tooltip withArrow multiline w={320} label={r.flag ? `${r.flag} Click to open it in Securities.` : 'Open in Securities'}>
                        <Anchor component={Link} to={securityLink(r.isin)}
                          size="sm" c={r.flag ? 'red' : undefined}>
                          {r.name}{r.flag && <> <IconAlertTriangle size={12} /></>}
                        </Anchor>
                      </Tooltip>
                    ) : <Text size="sm">{r.name}</Text>}
                  </Table.Td>
                  <Table.Td><Text size="sm" ff="monospace">{r.isin ?? (r.kind === 'cash' ? 'Cash' : '')}</Text></Table.Td>
                  <Table.Td><Text size="sm">{r.currency ?? ''}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {editing ? (
                      <NumberInput size="xs" value={r.weight} onChange={(v) => setWeight(r.key, v)} decimalScale={2}
                        min={0} max={100} hideControls suffix="%" styles={{ input: { textAlign: 'right' } }} />
                    ) : differs ? (
                      <Tooltip withArrow label={`Typed ${Number(r.weight).toFixed(2)}%; kept as ${Number(kept).toFixed(6)}% so the bucket adds up to exactly 100%.`}>
                        <Text size="sm" td="underline dotted">{Number(r.weight).toFixed(2)}%</Text>
                      </Tooltip>
                    ) : <Text size="sm">{Number(r.weight).toFixed(2)}%</Text>}
                  </Table.Td>
                  {editing && (
                    <Table.Td>
                      <Tooltip withArrow label="Remove from the bucket">
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => remove(r.key)} aria-label="Remove">
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th colSpan={3}>Total</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>
                <Tooltip withArrow multiline w={320} disabled={!editing || totalH === 10000}
                  label={Math.abs(gapH) <= toleranceH
                    ? `${fmtPct(Math.abs(gapH))} ${gapH > 0 ? 'short of' : 'over'} 100%: on Save it is spread over the members in proportion to their weights.`
                    : 'More than 0.05% away from 100%: adjust the weights before saving.'}>
                  <Text size="sm" fw={600} c={editing ? totalColor : undefined}>{fmtPct(totalH)}</Text>
                </Tooltip>
              </Table.Th>
              {editing && <Table.Th />}
            </Table.Tr>
          </Table.Tfoot>
        </Table>

        {editing && (
          <Group gap="sm" align="flex-end">
            <Tooltip withArrow multiline w={320} label="Add an Approved security: type part of its name, ISIN, BB Ticker or FIGI. Securities that are not Approved cannot be added.">
              <Select size="xs" w={480} placeholder="+ Add a security (name, ISIN, BB Ticker, FIGI)" data={addable}
                searchable clearable value={null} limit={50} onChange={addSecurity}
                nothingFoundMessage={secsQ.isLoading ? 'Loading…' : 'No Approved security matches (or it is already in the bucket)'}
                comboboxProps={{ withinPortal: true }} />
            </Tooltip>
            <Tooltip withArrow label="Add a cash line; one per currency.">
              <Select size="xs" w={180} placeholder="+ Add cash (currency)" data={ccys} searchable clearable value={null}
                onChange={addCash} comboboxProps={{ withinPortal: true }} />
            </Tooltip>
          </Group>
        )}
      </Stack>

      <Modal opened={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete bucket" centered>
        <Stack>
          {/* M2: a bucket used by a model cannot be deleted — said loudly, Delete greyed (as a
              security in a bucket, Pedro 2026-10-26: "yes, with loud warning"). */}
          {inModels.length > 0 && (
            <Alert color="red" variant="filled" icon={<IconAlertTriangle size={18} />}
              title="This bucket is used by a model and cannot be deleted">
              It is part of {inModels.map((m) => m.name).join(', ')}. Remove it from those models first;
              then it can be deleted here.
            </Alert>
          )}
          <Text size="sm">Delete <b>{bucket?.name}</b>? It leaves the list; its history stays in the Audit log.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <DisabledTooltipButton color="red" onClick={() => void doDelete()}
              disabledReason={inModels.length ? 'A bucket used by a model cannot be deleted; remove it from the model(s) first.' : undefined}>
              Delete bucket</DisabledTooltipButton>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

// ── Effective weights (M3) ───────────────────────────────────────────────────

/** M3 (Pedro "Start M3"): the model's weight in each security and cash line = bucket % of the
 *  model × member % of the bucket, a line in two buckets summed (C-302 §6). Kept at full
 *  precision; shown with 2 decimals (C-400). Not-approved lines red, as in buckets. */
function ModelWeightsSection({ model }: { model: ModelPortfolio }) {
  const q = useQuery({ queryKey: ['models', 'weights', model.id, model.updated_at], queryFn: () => modelsApi.weights(model.id) });
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await modelsApi.downloadWeights(model.id, model.name); }
    catch (e) { notifications.show({ color: 'red', title: 'Download failed', message: errMsg(e) }); }
    finally { setBusy(false); }
  };
  const pct2 = (s: string) => `${Number(s).toFixed(2)}%`;
  return (
    <Stack gap={6} mt="sm">
      <Group justify="space-between">
        <Tooltip withArrow multiline w={360} label="Each line's weight in the whole model: the bucket's % of the model × the line's % of the bucket, added up when a line sits in several buckets.">
          <Title order={5}>Weight in the model, by security</Title>
        </Tooltip>
        <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} loading={busy}
          onClick={() => void download()} disabled={!q.data}>Download (Excel)</Button>
      </Group>
      {q.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
      {q.isError && <Text size="sm" c="red">The weights could not be loaded: {errMsg(q.error)}</Text>}
      {q.data && (
        <Table verticalSpacing={4} withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Security / cash</Table.Th><Table.Th>ISIN</Table.Th><Table.Th>Ccy</Table.Th>
              <Table.Th>From buckets</Table.Th>
              <Table.Th style={{ textAlign: 'right', width: 150 }}>Weight in model</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {q.data.rows.length === 0 && (
              <Table.Tr><Table.Td colSpan={5}><Text size="sm" c="dimmed">The model's buckets have no members.</Text></Table.Td></Table.Tr>
            )}
            {q.data.rows.map((r) => (
              <Table.Tr key={`${r.kind}:${r.security_id ?? r.currency}`} style={r.flag ? { background: 'var(--mantine-color-red-0)' } : undefined}>
                <Table.Td>
                  {r.kind === 'security' && r.security_id ? (
                    <Tooltip withArrow multiline w={320} label={r.flag ? `${r.flag} Click to open it in Securities.` : 'Open in Securities'}>
                      <Anchor component={Link} size="sm" c={r.flag ? 'red' : undefined}
                        to={securityLink(r.isin)}>
                        {r.name}{r.flag && <> <IconAlertTriangle size={12} /></>}
                      </Anchor>
                    </Tooltip>
                  ) : <Text size="sm">{r.name}</Text>}
                </Table.Td>
                <Table.Td><Text size="sm" ff="monospace">{r.isin ?? (r.kind === 'cash' ? 'Cash' : '')}</Text></Table.Td>
                <Table.Td><Text size="sm">{r.currency ?? ''}</Text></Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {r.sources.map((s) => (s.bucket_id == null ? `Held directly ${pct2(s.bucket_pct)}`
                      : `${s.bucket ?? '?'} ${pct2(s.bucket_pct)} × ${pct2(s.member_pct)}`)).join(' + ')}
                  </Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{pct2(r.weight_pct)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th colSpan={4}>Total · {q.data.security_count} securities, {q.data.cash_count} cash</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{pct2(q.data.total_pct)}</Table.Th>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      )}
    </Stack>
  );
}

// ── Models (M2) ──────────────────────────────────────────────────────────────

const NO_MANAGE_MODELS = 'Changing models needs the "model.manage" permission (Admin by default; granted per person in Admin → Permissions).';

function ModelsTab({ onOpenBucket }: { onOpenBucket: (id: string) => void }) {
  const { can } = usePermissions();
  const canManage = can('model.manage');
  const { confirmDiscardAsync } = useDirtyForm();
  const listQ = useQuery({ queryKey: ['models', 'models'], queryFn: () => modelsApi.models() });
  const items = listQ.data?.items ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const dirtyRef = useRef(false);
  const choose = async (next: string | null) => {
    if (next === open) return;
    if (dirtyRef.current && !(await confirmDiscardAsync())) return;
    setOpen(next);
  };
  const current = open && open !== 'new' ? items.find((m) => m.id === open) ?? null : null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">A model is built from buckets, each weighted as a % of the whole model; the weights add up to 100%.</Text>
        <DisabledTooltipButton leftSection={<IconPlus size={14} />} size="xs"
          disabledReason={canManage ? undefined : NO_MANAGE_MODELS} onClick={() => void choose('new')}>
          New model
        </DisabledTooltipButton>
      </Group>
      <Card withBorder padding={0}>
        <Table highlightOnHover verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr><Table.Th>Model</Table.Th><Table.Th>Buckets</Table.Th><Table.Th>Description</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {listQ.isLoading && <Table.Tr><Table.Td colSpan={3}><Text size="sm" c="dimmed">Loading…</Text></Table.Td></Table.Tr>}
            {!listQ.isLoading && items.length === 0 && (
              <Table.Tr><Table.Td colSpan={3}><Text size="sm" c="dimmed">No models yet.</Text></Table.Td></Table.Tr>
            )}
            {items.map((m) => (
              <Table.Tr key={m.id} onClick={() => void choose(m.id)}
                style={{ cursor: 'pointer', background: open === m.id ? 'var(--mantine-color-blue-0)' : undefined }}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500} c={m.flagged.length ? 'red' : undefined}>{m.name}</Text>
                    {m.flagged.length > 0 && (
                      <Tooltip withArrow multiline w={340}
                        label={`Bucket(s) with a member no longer Approved: ${m.flagged.join(', ')}. The model can still be used.`}>
                        <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={11} />}>
                          {m.flagged.length} bucket{m.flagged.length === 1 ? '' : 's'} not approved
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td><Text size="sm">{m.component_count}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{m.description ?? ''}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
      {open && (open === 'new' || current) && (
        <ModelEditor key={open} model={current} canManage={canManage} onOpenBucket={onOpenBucket}
          onDirty={(d) => { dirtyRef.current = d; }}
          onSaved={(m) => setOpen(m.id)} onDeleted={() => setOpen(null)} onClose={() => void choose(null)} />
      )}
    </Stack>
  );
}

/** A model line: a bucket, or a security held directly (M-SEC, Pedro 2026-10-26: "models being
 *  made of buckets or securities"). */
interface CompRow {
  kind: 'bucket' | 'security'; id: string; name: string; weight: string; flag: string | null;
  members: number | null; isin: string | null;
}

const toCompRows = (m: ModelPortfolio | null): CompRow[] => (m?.components ?? []).map((c) => ({
  kind: c.kind, id: c.component_id,
  name: c.name ?? (c.kind === 'security' ? '(security not in the master)' : '(deleted bucket)'),
  weight: Number(c.weight_entered).toFixed(2), flag: c.flag, members: c.member_count, isin: c.isin ?? null,
}));
const rowKey = (r: { kind: string; id: string }) => `${r.kind}:${r.id}`;

function ModelEditor({ model, canManage, onOpenBucket, onDirty, onSaved, onDeleted, onClose }: {
  model: ModelPortfolio | null;   // null = new
  canManage: boolean;
  onOpenBucket: (id: string) => void;
  onDirty: (dirty: boolean) => void;
  onSaved: (m: ModelPortfolio) => void;
  onDeleted: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { registerDirty, confirmDiscardAsync } = useDirtyForm();
  const isNew = model === null;
  const [editing, setEditing] = useState(isNew);
  const [name, setName] = useState(model?.name ?? '');
  const [description, setDescription] = useState(model?.description ?? '');
  const [rows, setRows] = useState<CompRow[]>(() => toCompRows(model));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toleranceH = 5;   // 0.05 % (the server re-checks on Save)

  useEffect(() => {
    if (editing || !model) return;
    setName(model.name); setDescription(model.description ?? ''); setRows(toCompRows(model));
  }, [model, editing]);
  const original = useMemo(() => JSON.stringify({ n: model?.name ?? '', d: model?.description ?? '', r: toCompRows(model) }), [model]);
  const dirty = editing && JSON.stringify({ n: name, d: description, r: rows }) !== original;
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);

  const bucketsQ = useQuery({ queryKey: ['models', 'buckets'], queryFn: () => modelsApi.buckets(), enabled: editing });
  // M-SEC: Approved securities can be added directly, beside the buckets.
  const secsQ = useQuery({ queryKey: ['securities', 'list', 'all'], queryFn: () => securitiesApi.list({}), enabled: editing });
  const addable = useMemo(() => {
    const inModel = new Set(rows.map(rowKey));
    const buckets = (bucketsQ.data?.items ?? []).filter((b) => !inModel.has(`bucket:${b.id}`))
      .map((b) => ({ value: `bucket:${b.id}`, label: `Bucket · ${b.name}${b.flagged.length ? ` (${b.flagged.length} not approved)` : ''}` }));
    const secs = (secsQ.data?.items ?? []).filter((s) => s.authorized && !inModel.has(`security:${s.id}`))
      .map((s) => ({ value: `security:${s.id}`, label: `Security · ${[s.isin, s.description, s.bb_ticker, s.figi, s.currency].filter(Boolean).join(' · ')}` }));
    return [{ group: 'Buckets', items: buckets }, { group: 'Approved securities', items: secs }];
  }, [bucketsQ.data, secsQ.data, rows]);

  const totalH = rows.reduce((t, r) => t + (Number.isNaN(hundredths(r.weight)) ? 0 : hundredths(r.weight)), 0);
  const badWeight = rows.find((r) => !(hundredths(r.weight) > 0 && hundredths(r.weight) <= 10000));
  const gapH = 10000 - totalH;
  const flagged = rows.filter((r) => r.flag);
  const saveReason = !name.trim() ? 'Give the model a name.'
    : rows.length === 0 ? 'Add at least one bucket or security.'
    : badWeight ? `Each weight must be above 0% and at most 100% (${badWeight.name}).`
    : Math.abs(gapH) > toleranceH ? `The weights add up to ${fmtPct(totalH)}; they must be within 0.05% of 100%.`
    : undefined;

  const save = async (): Promise<boolean> => {
    if (saveReason) { notifications.show({ color: 'red', title: 'Not saved', message: saveReason }); return false; }
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim() || null,
        components: rows.map((r) => ({ kind: r.kind, component_id: r.id, weight: (hundredths(r.weight) / 100).toFixed(2) })) };
      const saved = isNew ? await modelsApi.createModel(body) : await modelsApi.updateModel(model!.id, body);
      await qc.invalidateQueries({ queryKey: ['models'] });
      notifications.show({ color: 'green', title: 'Saved', message: gapH !== 0
        ? `${saved.name} saved; the ${fmtPct(Math.abs(gapH))} difference was spread over the lines so they add up to 100%.`
        : `${saved.name} saved.` });
      setName(saved.name); setDescription(saved.description ?? ''); setRows(toCompRows(saved));
      setEditing(false); onDirty(false);
      onSaved(saved);
      return true;
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: errMsg(e) });
      return false;
    } finally { setSaving(false); }
  };
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => registerDirty('model-editor', dirty, () => saveRef.current()), [dirty, registerDirty]);

  const discard = async () => {
    if (dirty && !(await confirmDiscardAsync())) return;
    if (isNew) { onDirty(false); onClose(); return; }
    setName(model!.name); setDescription(model!.description ?? ''); setRows(toCompRows(model)); setEditing(false);
  };
  const doDelete = async () => {
    try {
      await modelsApi.deleteModel(model!.id);
      await qc.invalidateQueries({ queryKey: ['models'] });
      notifications.show({ color: 'green', title: 'Deleted', message: `${model!.name} was deleted.` });
      setConfirmDelete(false); onDirty(false); onDeleted();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: errMsg(e) });
    }
  };
  const addComponent = (v: string | null) => {
    if (!v) return;
    const [kind, id] = v.split(':', 2);
    if (kind === 'bucket') {
      const b = (bucketsQ.data?.items ?? []).find((x) => x.id === id);
      if (!b) return;
      setRows((p) => [...p, { kind: 'bucket', id: b.id, name: b.name, weight: '', members: b.member_count, isin: null,
        flag: b.flagged.length ? `Bucket with a member no longer Approved: ${b.flagged.join(', ')}.` : null }]);
    } else {
      const s = (secsQ.data?.items ?? []).find((x) => x.id === id);
      if (!s) return;
      setRows((p) => [...p, { kind: 'security', id: s.id, name: s.description, weight: '', members: null, isin: s.isin, flag: null }]);
    }
  };
  const setWeight = (key: string, v: string | number) => setRows((p) => p.map((r) => (rowKey(r) === key ? { ...r, weight: String(v) } : r)));
  const remove = (key: string) => setRows((p) => p.filter((r) => rowKey(r) !== key));
  const totalColor = totalH === 10000 ? 'green' : Math.abs(gapH) <= toleranceH ? 'yellow.8' : 'red';
  const resolved = new Map((model?.components ?? []).map((c) => [rowKey({ kind: c.kind, id: c.component_id }), c.weight_pct]));

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          {editing ? (
            <Stack gap={6} style={{ flex: 1, maxWidth: 640 }}>
              <TextInput label="Model name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
              <Textarea label="Description" autosize minRows={1} value={description}
                onChange={(e) => setDescription(e.currentTarget.value)} />
            </Stack>
          ) : (
            <Stack gap={2}>
              <Title order={4} c={flagged.length ? 'red' : undefined}>{model?.name}</Title>
              {model?.description && <Text size="sm" c="dimmed">{model.description}</Text>}
            </Stack>
          )}
          <Group gap="xs">
            {editing ? (
              <>
                {!isNew && (
                  // TRADE-CONS 1b: a model a consolidation or portfolio follows cannot be deleted (greyed, named).
                  <DisabledTooltipButton size="xs" color="red" variant="subtle" leftSection={<IconTrash size={14} />}
                    disabledReason={model?.followed_by?.length
                      ? `Followed by ${model.followed_by.join(', ')}. Choose another model for them first (Portfolios → Assign to…).`
                      : undefined}
                    onClick={() => setConfirmDelete(true)}>Delete model</DisabledTooltipButton>
                )}
                <Button size="xs" variant="default" onClick={() => void discard()}>Discard</Button>
                <DisabledTooltipButton size="xs" loading={saving} disabledReason={saveReason ?? (!dirty && !isNew ? 'Nothing has changed.' : undefined)}
                  onClick={() => void save()}>Save changes</DisabledTooltipButton>
              </>
            ) : (
              <>
                <Button size="xs" variant="default" onClick={onClose}>Close</Button>
                <DisabledTooltipButton size="xs" disabledReason={canManage ? undefined : NO_MANAGE_MODELS}
                  onClick={() => setEditing(true)}>Edit</DisabledTooltipButton>
              </>
            )}
          </Group>
        </Group>

        {flagged.length > 0 && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            {flagged.length === 1 ? '1 line holds' : `${flagged.length} lines hold`} a security no longer Approved on
            Securities ({flagged.map((r) => r.name).join(', ')}). The model can still be used.
          </Alert>
        )}

        <Table verticalSpacing={4} withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Bucket / security</Table.Th>
              <Table.Th>Members / ISIN</Table.Th>
              <Table.Th style={{ textAlign: 'right', width: 170 }}>Weight (% of model)</Table.Th>
              {editing && <Table.Th style={{ width: 40 }} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && (
              <Table.Tr><Table.Td colSpan={editing ? 4 : 3}><Text size="sm" c="dimmed">Nothing yet — add a bucket or a security below.</Text></Table.Td></Table.Tr>
            )}
            {rows.map((r) => {
              const kept = resolved.get(rowKey(r));
              const differs = !editing && kept != null && Number(kept).toFixed(12) !== Number(r.weight).toFixed(12);
              return (
                <Table.Tr key={rowKey(r)} style={r.flag ? { background: 'var(--mantine-color-red-0)' } : undefined}>
                  <Table.Td>
                    {r.kind === 'bucket' ? (
                      <Tooltip withArrow multiline w={320} label={r.flag ? `${r.flag} Click to open the bucket.` : 'Open the bucket'}>
                        <Anchor size="sm" c={r.flag ? 'red' : undefined} onClick={() => onOpenBucket(r.id)}>
                          {r.name}{r.flag && <> <IconAlertTriangle size={12} /></>}
                        </Anchor>
                      </Tooltip>
                    ) : (
                      <Tooltip withArrow multiline w={320} label={r.flag ? `${r.flag} Click to open it in Securities.` : 'A security held directly · open in Securities'}>
                        <Anchor component={Link} size="sm" c={r.flag ? 'red' : undefined}
                          to={securityLink(r.isin)}>
                          {r.name}{r.flag && <> <IconAlertTriangle size={12} /></>}
                        </Anchor>
                      </Tooltip>
                    )}
                  </Table.Td>
                  <Table.Td><Text size="sm" ff={r.kind === 'security' ? 'monospace' : undefined}>{r.kind === 'security' ? r.isin : r.members}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {editing ? (
                      <NumberInput size="xs" value={r.weight} onChange={(v) => setWeight(rowKey(r), v)} decimalScale={2}
                        min={0} max={100} hideControls suffix="%" styles={{ input: { textAlign: 'right' } }} />
                    ) : differs ? (
                      <Tooltip withArrow label={`Typed ${Number(r.weight).toFixed(2)}%; kept as ${Number(kept).toFixed(6)}% so the model adds up to exactly 100%.`}>
                        <Text size="sm" td="underline dotted">{Number(r.weight).toFixed(2)}%</Text>
                      </Tooltip>
                    ) : <Text size="sm">{Number(r.weight).toFixed(2)}%</Text>}
                  </Table.Td>
                  {editing && (
                    <Table.Td>
                      <Tooltip withArrow label="Remove from the model">
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => remove(rowKey(r))} aria-label="Remove">
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th colSpan={2}>Total</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>
                <Tooltip withArrow multiline w={320} disabled={!editing || totalH === 10000}
                  label={Math.abs(gapH) <= toleranceH
                    ? `${fmtPct(Math.abs(gapH))} ${gapH > 0 ? 'short of' : 'over'} 100%: on Save it is spread over the lines in proportion to their weights.`
                    : 'More than 0.05% away from 100%: adjust the weights before saving.'}>
                  <Text size="sm" fw={600} c={editing ? totalColor : undefined}>{fmtPct(totalH)}</Text>
                </Tooltip>
              </Table.Th>
              {editing && <Table.Th />}
            </Table.Tr>
          </Table.Tfoot>
        </Table>

        {editing && (
          <Tooltip withArrow multiline w={340} label="Add a bucket, or an Approved security held directly (type part of its name, ISIN, BB Ticker or FIGI). Securities that are not Approved cannot be added.">
            <Select size="xs" w={520} placeholder="+ Add a bucket or security" data={addable} searchable clearable value={null}
              onChange={addComponent} comboboxProps={{ withinPortal: true }} limit={60}
              nothingFoundMessage={bucketsQ.isLoading || secsQ.isLoading ? 'Loading…' : 'No other bucket or Approved security matches'} />
          </Tooltip>
        )}
        {!editing && model && <ModelWeightsSection model={model} />}
      </Stack>

      <Modal opened={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete model" centered>
        <Stack>
          <Text size="sm">Delete <b>{model?.name}</b>? It leaves the list; its buckets stay; its history stays in the Audit log.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button color="red" onClick={() => void doDelete()}>Delete model</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
